// Product Zoom Integration
// Opens the custom zoom viewer for product images on all viewports
// Replaces Dawn's image modal with pan & zoom functionality

(function() {
  'use strict';

  function enableProductZoom() {
    // Initialize in all environments (including theme editor for testing)

    // Create zoom viewer on demand if it doesn't exist (lazy loading)
    if (!window.productZoomViewer) {
      if (typeof window.createProductZoomViewer === 'function') {
        window.createProductZoomViewer();
      }

      // If still not available, wait and retry
      if (!window.productZoomViewer) {
        setTimeout(enableProductZoom, 100);
        return;
      }
    }

    // Find all product modal openers for images
    const modalOpeners = document.querySelectorAll('.product__modal-opener--image');

    modalOpeners.forEach((modalOpener, index) => {
      // Skip if already initialized
      if (modalOpener.hasAttribute('data-zoom-initialized')) {
        return;
      }

      // Get the button and image elements
      const button = modalOpener.querySelector('.product__media-toggle');
      const image = modalOpener.querySelector('img');

      if (!image) {
        return;
      }

      // CRITICAL: Disable Dawn's modal button click behavior by cloning the button
      // This removes ALL existing event listeners (including Dawn's modal opener)
      if (button) {
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
      }

      // Track swipe gestures to differentiate from clicks
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartTime = 0;
      let hasMoved = false;

      // Track touch start
      const onTouchStart = (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
        hasMoved = false;
      };

      // Track if user is swiping
      const onTouchMove = (e) => {
        const deltaX = Math.abs(e.touches[0].clientX - touchStartX);
        const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
        // If moved more than 10px, consider it a swipe/scroll
        if (deltaX > 10 || deltaY > 10) {
          hasMoved = true;
        }
      };

      // Handle tap to open zoom
      const onTap = (e) => {
        // Check if it was a quick tap (not a long press or swipe)
        const touchDuration = Date.now() - touchStartTime;
        const isQuickTap = touchDuration < 500;

        // Only prevent default for actual clicks/taps, not during scrolling
        if (e.type === 'click' || (e.type === 'touchend' && !hasMoved && isQuickTap)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Get all images
          const allImages = collectProductImages();

          // Find current image by comparing the actual image elements
          const currentImage = modalOpener.querySelector('img');
          const currentIndex = allImages.findIndex(img => img.element === currentImage);

          // Open the zoom viewer (use button for focus return, fallback to modalOpener)
          if (window.productZoomViewer) {
            window.productZoomViewer.open(
              allImages,
              Math.max(0, currentIndex),
              button || modalOpener
            );
          }
        }
      };

      // Attach listeners
      modalOpener.addEventListener('touchstart', onTouchStart, { passive: true });
      modalOpener.addEventListener('touchmove', onTouchMove, { passive: true });
      modalOpener.addEventListener('touchend', onTap, { passive: false, capture: true });
      modalOpener.addEventListener('click', onTap, { passive: false, capture: true });

      // Mark as initialized
      modalOpener.setAttribute('data-zoom-initialized', 'true');
    });

    // Fix height on scroll by triggering the theme's existing logic
    setupScrollHeightFix();
  }

  /**
   * Collect all product images from the gallery
   */
  function collectProductImages() {
    const images = [];
    const mediaItems = document.querySelectorAll('.product__media-item img');

    mediaItems.forEach((img, idx) => {
      // Get the highest resolution image from srcset or current src
      let highResSrc = img.currentSrc || img.src;

      // Try to get the largest image from srcset
      if (img.srcset) {
        const srcsetEntries = img.srcset.split(',').map(entry => {
          const parts = entry.trim().split(' ');
          return {
            url: parts[0],
            width: parts[1] ? parseInt(parts[1]) : 0
          };
        });

        // Sort by width and get largest
        srcsetEntries.sort((a, b) => b.width - a.width);
        if (srcsetEntries[0]) {
          highResSrc = srcsetEntries[0].url;
        }
      }

      images.push({
        src: highResSrc,
        alt: img.alt || '',
        width: img.naturalWidth,
        height: img.naturalHeight,
        element: img // Store reference to original element for matching
      });
    });

    return images;
  }

  function setupScrollHeightFix() {
    // Find the media-gallery custom element
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return;

    const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer || !viewer.slider) return;

    const slider = viewer.slider;
    let scrollTimeout;

    // Listen for scroll and update the active item
    slider.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Calculate which slide is active based on scroll position
        const scrollLeft = slider.scrollLeft;
        const slideWidth = slider.querySelector('.product__media-item')?.offsetWidth || 0;

        if (slideWidth === 0) return;

        const currentIndex = Math.round(scrollLeft / slideWidth);
        const slides = slider.querySelectorAll('.product__media-item');
        const currentSlide = slides[currentIndex];

        if (currentSlide && !currentSlide.classList.contains('is-active')) {
          // Remove is-active from all slides
          slides.forEach(slide => slide.classList.remove('is-active'));
          // Add is-active to current slide
          currentSlide.classList.add('is-active');

          // Trigger the theme's updateViewerHeight method
          if (mediaGallery.updateViewerHeight) {
            mediaGallery.updateViewerHeight();
          }
        }
      }, 100);
    }, { passive: true });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableProductZoom);
  } else {
    enableProductZoom();
  }

  // Re-run if the page content changes (for dynamic loading, variant switches, etc.)
  // But don't re-run if the zoom viewer is currently open or cart drawer is active
  if (!window.mobileZoomMutationObserver) {
    window.mobileZoomMutationObserver = new MutationObserver(() => {
      // Don't re-initialize if zoom viewer is currently open
      if (window.productZoomViewer?.state?.isOpen) {
        return;
      }

      // Don't re-initialize if cart drawer is currently open/animating
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && (cartDrawer.classList.contains('active') || cartDrawer.classList.contains('animate'))) {
        return;
      }

      // Debounce to avoid running too frequently
      clearTimeout(window.mobileModalFixTimeout);
      window.mobileModalFixTimeout = setTimeout(() => {
        enableProductZoom();
      }, 500);
    });

    window.mobileZoomMutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Expose cleanup function for memory leak prevention
  window.disconnectMobileZoomObserver = function() {
    if (window.mobileZoomMutationObserver) {
      window.mobileZoomMutationObserver.disconnect();
      window.mobileZoomMutationObserver = null;
    }
  };

})();
